import { BrowserClient, Hub } from '@sentry/browser';

import PaymentError from '../components/PaymentError';
import { DefaultConfig } from '../config/Config';
import { Environment, Nullable } from '../types';

type SentryServiceState =
  | {
      status: 'UN_INITIALIZED';
    }
  | {
      status: 'INITIALIZED';
      hub: Hub;
    };

export type SentryError = {
  message?: string;
  retryable?: boolean;
  error?: unknown;
};

let sentryService: Nullable<SentryService> = null;

export function getSentryService(env: Environment, defaultConfig: DefaultConfig) {
  if (sentryService === null) {
    const { appVersion, sentryDsn } = defaultConfig;
    sentryService = new SentryService(sentryDsn, env, appVersion);
  } else {
    sentryService.maybeReset(env);
  }

  return sentryService;
}

export const captureException = (error: SentryError): PaymentError => {
  return sentryService !== null ? sentryService.captureException(error) : new PaymentError(error);
};

class SentryService {
  private env: Environment = 'dev';
  private appVersion: string;
  private dsn: string;
  private state: SentryServiceState = { status: 'UN_INITIALIZED' };
  private errorQueue: Error[] = [];

  constructor(dsn: string, env: Environment, appVersion: string) {
    this.env = env;
    this.dsn = dsn;
    this.appVersion = appVersion;
    this.initializeSentry();
  }

  private initializeSentry = (whitelistUrls?: RegExp[]) => {
    try {
      const hub = new Hub(
        new BrowserClient({ dsn: this.dsn, environment: this.env, whitelistUrls }),
      );
      hub.configureScope((scope) => {
        scope.setTags({ version: this.appVersion });
      });
      this.state = {
        status: 'INITIALIZED',
        hub,
      };
    } catch (error) {
      this.errorQueue.push(error as Error);
    }
  };

  private processErrorQueue = (hub: Hub) => {
    this.errorQueue.forEach((error) => hub.captureException(error));
    this.errorQueue = [];
  };

  public captureException = (sentryError: SentryError): PaymentError => {
    const isNotProdEnv = this.env !== 'prod';

    if (this.state.status === 'UN_INITIALIZED') {
      this.initializeSentry();
    }

    const paymentError = new PaymentError(sentryError);

    if (this.state.status === 'INITIALIZED') {
      this.processErrorQueue(this.state.hub);

      if (sentryError.error) {
        // if we have an explicit error, then that is what we should send to Sentry
        this.state.hub.captureException(sentryError.error);
        if (isNotProdEnv) console.error(sentryError.error);
      } else {
        this.state.hub.captureException(paymentError);
        if (isNotProdEnv) console.error(paymentError);
      }
    } else if (sentryError.error) {
      // as above, if we have an explicit error, then that is what we should queue
      this.errorQueue.push(sentryError.error as Error);
    } else {
      this.errorQueue.push(paymentError);
    }

    return paymentError;
  };

  public maybeReset(env: Environment) {
    if (this.env !== env) {
      const oldEnv = this.env;
      this.env = env;
      this.initializeSentry();
      this.captureException(new Error(`SentryService switch from environment ${oldEnv} to ${env}`));
    }
  }
}

export default SentryService;
